import styled, { keyframes } from "styled-components";

/**
 * Allows the user to hide and show preview panes.
 * @module PreviewControl
 * @category Display Components
 */
import { focusVisible, measures } from "@quickpoint.me/shared";

const glow = keyframes`
  0% {
    filter: drop-shadow(0 0 2px red);
  }
  50% {
    filter: drop-shadow(0 0 8px red);
  }
  100% {
    filter: drop-shadow(0 0 2px red);
  }
`;

export const Label = styled.div`
    font-size: .85rem;
    font-family: ${(props) => props.theme.font};
    margin-left: 0.26svw;
`;

export const TextFormatLabel = styled.div`
    font-size: .85rem;
    font-family: ${(props) => props.theme.font};
    padding-inline-start: .25rem;
    opacity: .99;


`;

const StyledControl = styled.button<{
	$dim: boolean;
	$label?: string;
	$column?: boolean; // sets the flex direction to change the label position
	$isInPortrait: boolean;
	$height: string;
	$iconHeight: string;
	$collapseMargin?: boolean;
	$glow?: boolean;
}>`
    align-items: center;
    background: none;
    border: none;
    height: ${(props) => props.$height};
    justify-content: ${(props) => (props.$isInPortrait ? "space-evenly" : "unset")};
    cursor: pointer;
    display: flex;
    font-family: ${(props) => props.theme.font};
    flex-direction: ${(props) => (props.$column ? "column" : "row")};
    padding: 0;
    margin-left: ${(props) =>
			props.$isInPortrait
				? measures.textControlBar.portrait.iconMargin
				: measures.textControlBar.landscape.iconMargin};
    color: ${(props) => {
			if (props.$dim) return "#595959";
			if (props.$glow) return "red";
			return props.theme.navigationText;
		}};
    cursor: ${(props) => (props.$dim ? "not-allowed" : "pointer")};
    ${(props) => (props.$collapseMargin ? "margin-left: unset;" : "")}
    ${focusVisible}
    svg {
      height: ${(props) => props.$iconHeight};
      animation: ${(props) => (props.$glow ? glow : "none")} 2s linear infinite;
      @media (prefers-reduced-motion: reduce) {
        animation: none;
      }
    }
    svg::hover {
      height: ${(props) => props.$iconHeight};
    }
    svg path {
      fill: ${(props) => {
			if (props.$dim) return "#595959";
			if (props.$glow) return "red";
			return props.theme.navigationIcon;
		}};
    }
    svg:hover path {
      fill: ${(props) => (props.$dim ? "#595959" : props.theme.buttonHover)};
    }
`;

export const StyledTextControl = styled.button<{
	$dim: boolean;
	$label?: string;
	$column?: boolean; // sets the flex direction to change the label position
	$isInPortrait: boolean;
	$collapseMargin?: boolean;
}>`
    align-items: center;
    background: none;
    border: none;
    color: inherit;
    cursor: pointer;
    display: flex;
    justify-content: center;
    /* WCAG 2.5.5: comfortable target even for small glyphs like undo */
    min-width: 48px;
    min-height: 48px;
    flex-direction: ${(props) => (props.$column ? "column" : "row")};
    font: inherit;
    color: ${(props) => (props.$dim ? "#767676" : "inherit")};
    cursor: ${(props) => (props.$dim ? "not-allowed" : "pointer")};
    margin-left: ${(props) =>
			props.$isInPortrait && !props.$collapseMargin
				? measures.textControlBar.portrait.iconMargin
				: measures.textControlBar.landscape.iconMargin};
    ${focusVisible}
    svg {
      height: ${(props) =>
				props.$isInPortrait
					? measures.textControlBar.portrait.formatIcon
					: measures.textControlBar.landscape.formatIcon};
    }
`;

/**
 * TopBarControl - text-only button for the top bar
 */
export const TopBarControl = styled.button<{
	$isInPortrait: boolean;
}>`
    background: none;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    /* full bar height + wide padding: 48px gold-standard target on both axes */
    height: 100%;
    min-height: 48px;
    padding: 0 ${measures.topBar.landscape.controlPaddingInline};
    margin-left: ${(props) =>
		props.$isInPortrait
			? measures.topBar.portrait.iconMargin
			: measures.topBar.landscape.iconMargin};
    color: #333;
    font-family: ${(props) => props.theme.font};
    font-size: 0.9rem;
    font-weight: 500;
    white-space: nowrap;
    transition: color 0.15s ease, background-color 0.15s ease;

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }

    ${focusVisible}

    &:hover, &:focus-visible {
      color: #1a1a1a;
      background-color: ${(props) => props.theme.navigationFrame};
    }
`;

export default StyledControl;
